// Kids accounts see alerts as a friendly modal in the kids theme, not a small
// toast. The app raises alerts with sonner's `toast.*` from many places, so for
// a kids account those calls are redirected into this queue and shown one at a
// time by KidsAlertHost. Adult accounts are untouched.

export type KidsAlertKind = "success" | "error" | "info" | "warning";

export interface KidsAlert {
  id: string;
  kind: KidsAlertKind;
  title: string;
  description?: string;
  actionLabel?: string;
  onAction?: () => void;
}

export const MAX_QUEUED_ALERTS = 5;
/** An identical alert raised again within this time is a repeat, not news. */
export const DUPLICATE_WINDOW_MS = 1500;

type Listener = (queue: readonly KidsAlert[]) => void;

let queue: KidsAlert[] = [];
let lastSeen = new Map<string, number>();
const listeners = new Set<Listener>();
let counter = 0;

const notify = () => listeners.forEach((listener) => listener(queue));

export function subscribeToKidsAlerts(listener: Listener): () => void {
  listeners.add(listener);
  listener(queue);
  return () => {
    listeners.delete(listener);
  };
}

export function pushKidsAlert(
  alert: Omit<KidsAlert, "id">,
  now: number = Date.now(),
): string | null {
  const key = `${alert.kind}|${alert.title}|${alert.description ?? ""}`;
  const previous = lastSeen.get(key);
  lastSeen.set(key, now);
  if (lastSeen.size > 50) lastSeen = new Map([...lastSeen].slice(-25));
  if (previous !== undefined && now - previous < DUPLICATE_WINDOW_MS) return null;
  const id = `kids-alert-${++counter}`;
  queue = [...queue, { ...alert, id }].slice(-MAX_QUEUED_ALERTS);
  notify();
  return id;
}

export function dismissKidsAlert(id: string): void {
  queue = queue.filter((alert) => alert.id !== id);
  notify();
}

export function resetKidsAlertsForTest(): void {
  queue = [];
  lastSeen = new Map();
  counter = 0;
  listeners.clear();
}

interface SonnerLikeOptions {
  description?: unknown;
  action?: { label?: unknown; onClick?: () => void };
}
type ToastMethod = (message: unknown, options?: SonnerLikeOptions) => unknown;
type ToastApi = Record<string, unknown>;

const PATCHED: KidsAlertKind[] = ["success", "error", "info", "warning"];

/**
 * Redirects a sonner-style `toast` object's success/error/info/warning calls
 * into the kids queue. Only plain text is redirected; anything else (custom
 * JSX, loading and promise toasts) still goes to the original method. Returns a
 * function that puts the originals back.
 */
export function installKidsToastBridge(toast: ToastApi): () => void {
  const originals = new Map<string, unknown>();
  for (const kind of PATCHED) {
    const original = toast[kind] as ToastMethod | undefined;
    if (typeof original !== "function") continue;
    originals.set(kind, original);
    toast[kind] = (message: unknown, options?: SonnerLikeOptions) => {
      if (typeof message !== "string" || !message.trim()) return original(message, options);
      const description = typeof options?.description === "string" ? options.description : undefined;
      const actionLabel = typeof options?.action?.label === "string" ? options.action.label : undefined;
      return pushKidsAlert({ kind, title: message, description, actionLabel, onAction: options?.action?.onClick });
    };
  }
  return () => {
    for (const [kind, original] of originals) toast[kind] = original;
  };
}
